import React, { FC, Fragment, useState } from 'react';
import { Alert, Box, Button, Divider, FormControlLabel, Switch, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { LabClient, SubcontractDefaults } from 'models/client-directory';
import {
    subcontractDefaultsTemplate,
    updateSubcontractDefaultsTemplate,
} from 'apollo/templates/subcontract/subcontract.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import Loader from 'components/shared/loader/loader.component';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import InfoText from 'components/shared/info-text.component';
import SubcontractMessageDefaults from './subcontract-message-defaults.component';
import SubcontractResponsibleUsers from './subcontract-responsible-users.component';

type SubcontractDefaultsProps = {
    labClient: LabClient;
};

const NO_DEFAULTS: SubcontractDefaults = {
    includePatientReference: false,
    includePatientIdentityNumber: false,
    includeDentistName: false,
    includeAllRestorations: false,
    includeAttachments: false,
    includeScanCaseFiles: false,
    messageFields: [],
    messageLanguage: null,
    responsibleUserIds: [],
};

const defaultsOf = (stored: Partial<SubcontractDefaults> | null | undefined): SubcontractDefaults => ({
    ...NO_DEFAULTS,
    ...(stored ?? {}),
    messageFields: stored?.messageFields ?? [],
    messageLanguage: stored?.messageLanguage ?? null,
    responsibleUserIds: stored?.responsibleUserIds ?? [],
});

/** The categories in the order the subcontract flow asks about them. */
type ContentCategory = Exclude<keyof SubcontractDefaults, 'messageFields' | 'messageLanguage' | 'responsibleUserIds'>;

const CATEGORIES: { key: ContentCategory; label: string }[] = [
    { key: 'includeAllRestorations', label: 'orders:SUBCONTRACT_DEFAULT_ALL_RESTORATIONS' },
    { key: 'includeAttachments', label: 'orders:SUBCONTRACT_DEFAULT_ATTACHMENTS' },
    { key: 'includeScanCaseFiles', label: 'orders:SUBCONTRACT_DEFAULT_SCAN_CASE_FILES' },
    { key: 'includePatientReference', label: 'orders:SUBCONTRACT_INCLUDE_PATIENT' },
    { key: 'includePatientIdentityNumber', label: 'orders:SUBCONTRACT_INCLUDE_PATIENT_IDENTITY_NUMBER' },
    { key: 'includeDentistName', label: 'orders:SUBCONTRACT_DEFAULT_DENTIST_NAME' },
];

/**
 * DD-3485 Marketplace: what this lab sends by default when it subcontracts to this
 * supplier. Defaults only pre-fill the subcontract flow — every sub-order is still
 * confirmed item by item — and changing them never alters sub-orders already sent. Only
 * this lab sees them; the supplier never learns how it is set up here.
 */
const SubcontractDefaultsSettings: FC<SubcontractDefaultsProps> = ({ labClient }) => {
    const { t: translate } = useTranslation(['orders', 'common']);
    const { showBoundary } = useDenthubErrorBoundary();
    const [defaults, setDefaults] = useState<SubcontractDefaults>(NO_DEFAULTS);
    const [isSaved, setIsSaved] = useState(false);
    const supplierId = labClient.labId ?? labClient.lab?._id;

    const { loading } = useQuery(subcontractDefaultsTemplate, {
        fetchPolicy: 'no-cache',
        variables: { supplierId },
        onCompleted: ({ supplier }) => setDefaults(defaultsOf(supplier?.subcontractDefaults)),
        onError(error) {
            showBoundary(error);
        },
    });

    const [saveDefaults, { loading: saving }] = useMutation(updateSubcontractDefaultsTemplate, {
        onCompleted: ({ updateSubcontractDefaults }) => {
            setDefaults(defaultsOf(updateSubcontractDefaults?.subcontractDefaults));
            setIsSaved(true);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const toggle = (key: ContentCategory, checked: boolean) => {
        setIsSaved(false);
        setDefaults((previous) => ({
            ...previous,
            [key]: checked,
            // The identity number never travels without the patient details.
            ...(key === 'includePatientReference' && !checked ? { includePatientIdentityNumber: false } : {}),
        }));
    };

    const updateDefaults = (updates: Partial<SubcontractDefaults>) => {
        setIsSaved(false);
        setDefaults((previous) => ({ ...previous, ...updates }));
    };

    if (loading) return <Loader />;

    // Exactly the input's fields — a query result also carries __typename.
    const defaultsInput = {
        ...Object.fromEntries(CATEGORIES.map((category) => [category.key, defaults[category.key]])),
        messageFields: defaults.messageFields,
        messageLanguage: defaults.messageLanguage,
        responsibleUserIds: defaults.responsibleUserIds,
    };

    return (
        <Fragment>
            <Box mb={2}>
                <Typography variant="h2" mb={1}>
                    {translate('orders:SUBCONTRACT_DEFAULTS')}
                </Typography>
                <InfoText text={translate('orders:SUBCONTRACT_DEFAULTS_INFO')} />
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" flexDirection="column" mb={2}>
                {CATEGORIES.map((category) => (
                    <FormControlLabel
                        key={category.key}
                        sx={category.key === 'includePatientIdentityNumber' ? { ml: 4 } : undefined}
                        control={
                            <Switch
                                checked={defaults[category.key]}
                                disabled={
                                    category.key === 'includePatientIdentityNumber' && !defaults.includePatientReference
                                }
                                onChange={(_event, checked) => toggle(category.key, checked)}
                                data-test={`subcontract-defaults.${category.key}`}
                            />
                        }
                        label={translate(category.label)}
                    />
                ))}
            </Box>
            <Alert severity="info" sx={{ mb: 2 }}>
                {translate('orders:SUBCONTRACT_DEFAULTS_PATIENT_NOTE')}
            </Alert>
            <Divider sx={{ mb: 2 }} />
            <SubcontractResponsibleUsers
                userIds={defaults.responsibleUserIds}
                onChange={(responsibleUserIds) => updateDefaults({ responsibleUserIds })}
            />
            <Divider sx={{ mb: 2 }} />
            <SubcontractMessageDefaults
                fields={defaults.messageFields}
                language={defaults.messageLanguage}
                onFieldsChange={(messageFields) => updateDefaults({ messageFields })}
                onLanguageChange={(messageLanguage) => updateDefaults({ messageLanguage })}
            />
            <Box display="flex" justifyContent="flex-end">
                <Button
                    variant="contained"
                    disabled={saving}
                    onClick={() => saveDefaults({ variables: { supplierId, defaults: defaultsInput } })}
                    data-test="subcontract-defaults.save-btn"
                >
                    {translate('common:SAVE')}
                </Button>
            </Box>
            {isSaved ? (
                <AlertSnackbar type="success" message={translate('orders:SUBCONTRACT_DEFAULTS_SAVED')} />
            ) : undefined}
        </Fragment>
    );
};

export default SubcontractDefaultsSettings;
